import React, { useState } from 'react';
import { FiX, FiEdit2, FiTrash2, FiCheck, FiMail, FiKey } from 'react-icons/fi';
import axios from 'axios';
import { toast } from 'react-toastify';
// const url = import.meta.env.VITE_BACKEND_URL;
const url = process.env.REACT_APP_BACKEND_URL;

const ViewMember = ({ member, onEdit, onDelete, onClose, currentUser }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isRequestingChanges, setIsRequestingChanges] = useState(false);
  const [isSendingPin, setIsSendingPin] = useState(false);

  const handleDelete = async () => {
    if (!deleteReason) {
      toast.error('Please provide a reason for deletion');
      return;
    }

    if (window.confirm(`Are you sure you want to permanently delete ${member.fname} ${member.lname}? This action cannot be undone.`)) {
      setIsDeleting(true);
      try {
        await axios.delete(`${url}/api/members/${member._id}`, {
          data: { reason: deleteReason }
        });
        toast.success('Member deleted successfully!');
        onDelete();
        onClose();
      } catch (err) {
        console.error('Error deleting member:', err);
        const errorMsg = err.response?.data?.message || 'Delete failed';
        toast.error(errorMsg);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleSendPin = async () => {
    if (window.confirm(`Are you sure you want to send PIN to ${member.email}?`)) {
      setIsSendingPin(true);
      try {
        const response = await axios.post(`${url}/api/members/${member._id}/send-pin`);

        toast.success(
          <div>
            <div>PIN sent successfully!</div>
            <div style={{ fontSize: '0.9em', marginTop: '5px' }}>
              <FiMail style={{ marginRight: '5px' }} />
              PIN sent to {member.email}
            </div>
          </div>,
          { autoClose: 4000 }
        );
      } catch (err) {
        console.error('Error sending PIN:', err);

        // More detailed error handling
        if (err.response) {
          // The server responded with an error status
          const errorMsg = err.response.data.message || 'Failed to send PIN';
          toast.error(`Error: ${errorMsg}`);
        } else if (err.request) {
          // The request was made but no response was received
          toast.error('Network error: Could not connect to server');
        } else {
          // Something else happened
          toast.error('Unexpected error occurred');
        }
      } finally {
        setIsSendingPin(false);
      }
    }
  };

  const handleStatusUpdate = async (action, note = '') => {
    let setIsProcessing;

    switch (action) {
      case 'approve':
        setIsProcessing = setIsApproving;
        break;
      case 'reject':
        setIsProcessing = setIsRejecting;
        break;
      case 'needs_changes':
        setIsProcessing = setIsRequestingChanges;
        break;
      default:
        return;
    }

    setIsProcessing(true);

    try {
      const response = await axios.post(`${url}/api/members/${member._id}/action`, {
        action,
        note
      });

      // Show success message with email notification info
      toast.success(
        <div>
          <div>Member {action} successfully!</div>
          <div style={{ fontSize: '0.9em', marginTop: '5px' }}>
            <FiMail style={{ marginRight: '5px' }} />
            Email notification sent to {member.email}
          </div>
        </div>,
        { autoClose: 4000 }
      );

      // Refresh the member data
      onDelete();
    } catch (err) {
      console.error(`Error ${action} member:`, err);

      // Check if it's an email sending error specifically
      if (err.response?.data?.message?.includes('email') ||
        err.response?.data?.message?.includes('Email')) {
        const errorMsg = err.response.data.message;
        toast.error(
          <div>
            <div>Status updated but email failed to send</div>
            <div style={{ fontSize: '0.9em', marginTop: '5px' }}>
              {errorMsg}
            </div>
          </div>,
          { autoClose: 5000 }
        );

        // Still refresh since the status was updated
        onDelete();
      } else {
        const errorMsg = err.response?.data?.message || `${action} failed`;
        toast.error(errorMsg);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApprove = () => {
    if (window.confirm(`Are you sure you want to approve ${member.fname} ${member.lname}?`)) {
      handleStatusUpdate('approve');
    }
  };

  // const handleReject = () => {
  //   const reason = prompt('Please provide a reason for rejection:');
  //   if (!reason) return;

  //   if (window.confirm(`Are you sure you want to reject ${member.fname} ${member.lname}?`)) {
  //     handleStatusUpdate('reject', reason);
  //   }
  // };

  const handleNeedsChanges = async () => {
    const note = prompt('Please specify what changes are needed:');
    if (!note) return;

    if (window.confirm(`Are you sure you want to request changes and delete ${member.fname} ${member.lname}?`)) {
      try {
        // ✅ First update status with note
        await handleStatusUpdate('needs_changes', note);

        // ✅ Then delete member
        await axios.delete(`${url}/api/members/${member._id}`, {
          data: { reason: note }
        });

        toast.success('Changes requested and member deleted successfully!');
        onDelete();
        onClose();
      } catch (err) {
        console.error('Error deleting member:', err);
        const errorMsg = err.response?.data?.message || 'Delete failed';
        toast.error(errorMsg);
      } finally {
        setIsDeleting(false);
      }
    }
  };


  // Check if member can be approved (only for admin and if status is not approved)
  const canApprove = currentUser.role === 'approve' &&
    member.status &&
    member.status !== 'approved' &&
    ['pending', 'rejected', 'needs_changes'].includes(member.status);

  // Check if member can be rejected (only for admin and if status is not rejected)
  const canReject = currentUser.role === 'approve' &&
    member.status &&
    member.status !== 'rejected';

  // Check if member can be requested for changes (only for admin and if status is not needs_changes)
  const canRequestChanges = currentUser.role === 'approve' &&
    member.status &&
    member.status !== 'needs_changes';
  const canSendPin = currentUser.role === 'approve' &&
    member.status === 'approved';

  return (
    <div className="members-form-container">
      <div className="form-header">
        <h2>Member Details: {member.fname} {member.lname}</h2>
        <button onClick={onClose} className="close-btn">
          <FiX />
        </button>
      </div>

      <div className="view-member-container">
        {/* Admin actions */}
        {currentUser.role === 'approve' && (
          <div className="admin-actions">
            <h3>Admin Actions</h3>
            <div className="action-buttons">
              {canApprove && (
                <button
                  onClick={handleApprove}
                  className="approve-btn"
                  disabled={isApproving}
                >
                  <FiCheck /> {isApproving ? 'Approving...' : 'Approve Member'}
                </button>
              )}

               {/* {canReject && (
                <button 
                  onClick={handleReject} 
                  className="reject-btn"
                  disabled={isRejecting}
                >
                  {isRejecting ? 'Rejecting...' : 'Reject'}
                </button>
              )} */}

              {/* {canRequestChanges && (
                <button
                  onClick={handleNeedsChanges}
                  className="needs-changes-btn"
                  disabled={isRequestingChanges}
                >
                  {isRequestingChanges ? 'Requesting Changes...' : 'Needs Changes'}
                </button>
              )} */}

              {canSendPin && (
                <button
                  onClick={handleSendPin}
                  className="send-pin-btn"
                  disabled={isSendingPin}
                >
                  <FiKey />
                  {isSendingPin ? 'Sending PIN...' : 'Send PIN'}
                </button>
              )}

            </div>

            <div className="email-notification-note">
              <FiMail style={{ marginRight: '5px' }} />
              Email notifications will be sent to {member.email} for any status changes
            </div>
          </div>
        )}

        <div className="member-details-sections">
          {/* Personal Information */}
          <div className="details-section">
            <h3>Personal Information</h3>
            <div className="details-grid">
              <div className="detail-item">
                <label>Register Type</label>
                {/* <span>{member.registertype?.name || 'N/A'}</span> */}
                <span>
                  {member.registertype?.name || 'N/A'}
                  {member.registertype?.amount && ` - $${member.registertype.amount.toFixed(2)}`}
                </span>

              </div>
              <div className="detail-item">
                <label>Name</label>
                <span>{`${member.fname} ${member.mname || ''} ${member.lname}`}</span>
              </div>
              <div className="detail-item">
                <label>Mother's Name</label>
                <span>{member.moname || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Email</label>
                <span>{member.email || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Mobile</label>
                <span>{member.mobile || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Date of Birth</label>
                <span>{member.dob || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Place of Birth</label>
                <span>{member.pob || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Sex</label>
                <span>{member.sex || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Education Level</label>
                <span>{member.elevel || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Residence</label>
                <span>{member.residence || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Status</label>
                <span className={`status-badge status-${member.status || 'pending'}`}>
                  {member.status || 'pending'}
                </span>
              </div>
              {member.member_number && (
                <div className="detail-item">
                  <label>Member Number</label>
                  <span>{member.member_number}</span>
                </div>
              )}
              {member.admin_note && (
                <div className="detail-item full-width">
                  <label>Admin Note</label>
                  <span>{member.admin_note}</span>
                </div>
              )}
            </div>
          </div>

          {/* Other sections remain the same */}
          {/* Location Information */}
          <div className="details-section">
            <h3>Location Information</h3>
            <div className="details-grid">
              <div className="detail-item">
                <label>Country</label>
                <span>{member.country?.name || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Region</label>
                <span>{member.region?.name || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>District</label>
                <span>{member.district?.name || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Bank Information */}
          <div className="details-section">
            <h3>Bank Information</h3>
            <div className="details-grid">
              <div className="detail-item">
                <label>Payment Terms</label>
                <span>{member.PaymentTerms || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Bank Name</label>
                <span>{member.bankname || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Bank Address</label>
                <span>{member.bankaddress || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Account Number</label>
                <span>{member.accno || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Next of Kin */}
          <div className="details-section">
            <h3>Next of Kin Information</h3>
            <div className="details-grid">
              <div className="detail-item">
                <label>Next of Kin 1 Name</label>
                <span>{member.nokname1 || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Next of Kin 1 Address</label>
                <span>{member.nokaddress1 || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Next of Kin 1 Mobile</label>
                <span>{member.nokmobile1 || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Next of Kin 2 Name</label>
                <span>{member.nokname2 || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Next of Kin 2 Address</label>
                <span>{member.nokaddress2 || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Next of Kin 2 Mobile</label>
                <span>{member.nokmobile2 || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="details-section">
            <h3>Documents</h3>
            <div className="details-grid">
              <div className="detail-item">
                <label>Photo</label>
                {member.photo && (
                  <div className="document-preview">
                    <img src={`${url}/${member.photo}`} alt="Member" className="preview-image" />
                  </div>
                )}
              </div>
              <div className="detail-item">
                <label>Identity Document</label>
                {member.identity && (
                  <div className="document-preview">
                    {member.identity.endsWith('.pdf') ? (
                      <a href={`${url}/${member.identity}`} target="_blank" rel="noopener noreferrer" className="file-link">
                        View PDF Document
                      </a>
                    ) : (
                      <img src={`${url}/${member.identity}`} alt="Identity" className="preview-image" />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="view-actions">
          <button onClick={onEdit} className="edit-btn">
            <FiEdit2 /> Edit Member
          </button>

          <button onClick={onClose} className="cancel-btn">
            Back to List
          </button>
        </div>

        {showDeleteConfirm && (
          <div className="delete-confirmation">
            <h4>Delete Member: {member.fname} {member.lname}</h4>
            <p>Please provide a reason for deleting this member:</p>
            <textarea
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Enter reason for deletion..."
              rows="4"
            />
            <div className="delete-actions">
              <button onClick={handleDelete} className="confirm-delete-btn" disabled={isDeleting}>
                {isDeleting ? 'Deleting...' : 'Confirm Delete'}
              </button>
              <button onClick={() => setShowDeleteConfirm(false)} className="cancel-delete-btn">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewMember;
